/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
// -----TEMPORARY------- will remove after we have the feature apis of these things
const ActionTypes = require('gemini/lib/@waca/dashboard-common/dist/utils/ActionTypes');
import ToolbarHelper from './ToolbarHelper';
export const MULTI_MEASURES_SERIES = '_multiMeasuresSeries';
// ---------------------
import { getNumberOfFilterItems } from './utils';

/**
 * The VisSlotDataItemHandler is a helper class which handles assigning and moving DataItems in slots
 * Items can be inserted/replaced into slots from metadata (as provided by the metadata tree)
 * or come from other SlotDataItemHandler instances (when other slot items being moved or swapped).
 * The main purpose of this helper class is to support drag and drop in the slot editor.
 */
export default class VisSlotDataItemHandler {
	/**
	  * @param slotAPI - the slotAPI to bind this action handler to
	  * @param indexInSlot - the index of the data item of interest in this slotType
	  * @param cbGetActionsForSlot - a callback function that can provide the actions associated with this slot/dataItemModel
	  * @param visualization - the new visualization API
	  * @param dashboardApi - dashboardApi
	  * @param content - contentAPI associated with the slots
	  * @param fnAccepts - injected function to validate accepts, it is called if it is defined, currently used for V2 xtab
	  */
	constructor(slotAPI, indexInSlot, cbGetActionsForSlot, visualization, dashboardApi, content, fnAccepts) {
		this.content = content;
		this.dashboardApi = dashboardApi;
		this.transactionApi = dashboardApi.getFeature('Transaction');
		this.indexInSlot=indexInSlot;
		this._getActions=cbGetActionsForSlot;
		this.setSlotAPI(slotAPI);
		this._toolbarHelper = new ToolbarHelper();
		this.visualization = visualization;
		this.visDnD = this.content.getFeature('VisDnD');
		this.fnAccepts = fnAccepts;
	}

	setSlotAPI(slot) {
		this.slot = slot;
		this.slotId = slot.getDefinition().getId();
	}

	getSlotAPI() {
		return this.slot;
	}

	getContentId() {
		return this.content.getId();
	}

	getGraphicData() {
		// if this content supports the property `value.graphic.content`,
		// then we return the value of it, otherwise return null
		if(this.content.getPropertyNameList().indexOf('value.graphic.content') !== -1) {
			return this.content.getPropertyValue('value.graphic.content');
		}
		return null;
	}

	getDatasourceId() {
		const dataSource = this.visualization.getDataSource();
		if (dataSource) {
			return dataSource.getId();
		}
		return null;
	}

	getFilterStringAndNumber() {
		const result = {};
		const dataItem = this.slot.getDataItemList()[this.indexInSlot];
		const columnId = dataItem && dataItem.getColumnId();
		if (columnId && columnId !== MULTI_MEASURES_SERIES) {
			// Find the local filter which can be displayed under the dataItem.
			const localFilters = this.visualization.getLocalFilters();
			const localFiltersInfo = localFilters && localFilters.getFilterList();
			if(localFiltersInfo && localFiltersInfo.length) {
				const columnFilter = localFiltersInfo.find((localFilterInfo) => {
					if (localFilterInfo.readOnly) {
						return false;
					}
					if (localFilterInfo.filterBins) {
						if (dataItem.getBinning()) {
							return localFilterInfo.id === dataItem.getId();
						}
					} else {
						if(localFilterInfo.aggregationType) {
							return localFilterInfo.columnId === columnId && dataItem.getAggregation() === localFilterInfo.aggregationType;
						} else {
							// If the case is when there is an ordinal and attribute we dont want to
							// display the filter beneath it.
							return localFilterInfo.columnId === columnId;
						}
					}

					return false;
				});
				if (columnFilter) {
					// get filterString
					const filtersDesc = localFilters.getFiltersDesc();
					const columnFilterDesc = filtersDesc.find((filterDesc) => filterDesc.dataId === columnFilter.id);
					if(columnFilterDesc) {
						result.filterString = columnFilterDesc.description;
					}
					// get number of filter items
					result.numberOfFilterItems = getNumberOfFilterItems(columnFilter);
				}
			}
		}
		return result;
	}

	isMeasureGroupItem() {
		if (this._isMultiMeasuresSeriesSlot()) {
			var thisDataItem=this.slot.getDataItemList()[this.indexInSlot];
			return thisDataItem && thisDataItem.getColumnId() === '_multiMeasuresSeries';
		}
		return false;
	}

	// This function is copied from the static function SlotAPIUtil.isMultiMeasuresSeriesSlot in live-widget component
	_isMultiMeasuresSeriesSlot () {
		const dataItemList = this.slot.getDataItemList();
		return dataItemList.find(dataItem => {
			return dataItem.getColumnId() === MULTI_MEASURES_SERIES;
		}) ? true : false;
	}

	_getTargetIndex(dropAfter) {
		return this.indexInSlot + ((dropAfter) ? 1 : 0);
	}

	insertItemFromTypeInSlot(metadataColumn, sourceId, options) {
		const { replaceSlotItem } = options;
		const dataSource = this.dashboardApi.getFeature('DataSources').getDataSource(sourceId);
		if(dataSource) {
			const target = this.getDropTargetInfo(/*addAfter*/!replaceSlotItem);
			const source = {
				type: 'metadataColumns',
				data: [metadataColumn]
			};
			if (metadataColumn && metadataColumn.getType()) {
				//NOTE: onDrop will validate (call accepts)
				this.visDnD.onDrop(source, target);
			}
		}
	}

	/* check if this slot can have one more item in it*/
	canAcceptMoreItems() {
		return this._acceptsCommon(1, true);
	}

	/**
	 * @returns {number} of slot items in a slot
	 */
	getNumberItemsInSlot() {
		return this.slot.getDataItemList().length;
	}

	getDropTargetInfo(addAfter, additionalInfoOptions={}) {
		let dropTargetInfo = {
			type: 'slot.item',
			info: {
				contentId: this.getContentId(),
				slot: this.getSlotAPI(),
				indexInSlot: this.indexInSlot,
				addAfter: addAfter
			}
		};

		Object.keys(additionalInfoOptions).forEach((key) => {
			dropTargetInfo.info[key] = additionalInfoOptions[key];
		});
		return dropTargetInfo;
	}

	_acceptsCommon(countOfItemsToDrop, addAfter) {
		//IF this definition contains (or could contain) a multiMeasuresSeries item,
		//You CAN drop on or after in a values slot (even though values has maxItems of 1 => multi-items go where 'Measure Group (n)' is)
		if ((this.slotId ==='values' && this._isMultiMeasuresSeriesSlot()) ||
            (this.slot.getDefinition().isMultiMeasureSupported())) {
			return true;
		}

		//You CAN'T drop more items than the slot can allow.
		return !this._exceedsItemsLimit(countOfItemsToDrop, addAfter);
	}

	_exceedsItemsLimit(countOfItemsToDrop, addAfter) {
		const limit = this.slot.getDefinition().getMaxItems();
		if (typeof limit !== 'number' || limit < 0) {
			// no limit verification if maxItems is null or undefined or something else than a positive number (0 is considered as positive in this function)
			return false;
		}
		const mappedItems = this.slot.getDataItemList().length;
		//When dropping on top of an existing slot dataItem, we replace it with the items we're dropping.
		const mappedItemsAfterDrop = countOfItemsToDrop + mappedItems - ((addAfter) ? 0 : 1);
		return mappedItemsAfterDrop > limit;
	}

	/**
	 * Performs swaps of the item represented by the sourceDataItemHandler with the item represented by this handler
	 * either as a 'replace' or an 'addAfter' operation.
	 * NOTE: This function is now only used for the Slot to Slot A11Y case.
	 * @param sourceDataItemHandler - The handler representing the source DataItem being moved.
	 * @param addAfter (optional) - a boolean indicating if this is a replace or append wrt this dataItem/index.
	 */
	swapSlotItems(sourceDataItemHandler, addAfter) {
		return this.onDrop(
			//SourceInfo (as defined in DnDSlotItemHandler)
			{
				data: {
					source: {
						slot: sourceDataItemHandler.getSlotAPI(),
						indexInSlot: sourceDataItemHandler.indexInSlot
					}
				}
			},
			//TargetInfo (as defined in DnDSlotItemHandler)
			this.getDropTargetInfo(addAfter)
		);
	}

	/**
	 * @returns A11YData in the form that the provider needs to call insert.
	 */
	onDropA11Y(addAfter) {
		const target = this.getDropTargetInfo(addAfter);
		this.visDnD.onDrop({ type: 'A11YMetadataColumns' }, target);
	}

	//A generic handler where data is expected to be in the form built for drag and drop.
	onDrop(source, target) {
		//The only one we support locally for onDrop is slot sources.
		if (source.data.source) {
			// If we are swapping onto same item, we are done
			if (source.data.source.slot === this.getSlotAPI() && source.data.source.indexInSlot == this.indexInSlot) {
				return;
			}
			const sourceSlot = source.data.source.slot;
			const sourceIndex= source.data.source.indexInSlot;
			const addAfter = target.info.addAfter;
			const transactionToken = this.transactionApi.startTransaction();
			let sourceDataItem = sourceSlot.getDataItemList()[sourceIndex];
			let thisDataItem = this.slot.getDataItemList()[this.indexInSlot];
			let thisDataItemUniqueId = thisDataItem && thisDataItem.getId();

			if (sourceSlot.getId() === this.slotId) {
				// Handle swapping items within the same slot
				this._swapItemsInTheSameSlot(sourceDataItem.getId(), thisDataItemUniqueId, addAfter, transactionToken);
			} else if (addAfter || !thisDataItem) {
				this._moveItemToSlot(sourceSlot, sourceDataItem, addAfter, transactionToken);
			} else {
				this._swapItemsBetweenSlots(sourceSlot, sourceDataItem.getId(), thisDataItemUniqueId, transactionToken);
			}
			this.transactionApi.endTransaction(transactionToken);
		}
	}

	_swapItemsInTheSameSlot(sourceItemUniqueId, targetItemUniqueId, addAfter, transactionToken) {
		let dataItemRefs = this.slot.getDataItemList().map(dataItem => dataItem.getId());

		let sourceIndex = dataItemRefs.indexOf(sourceItemUniqueId);
		let targetIndex = dataItemRefs.indexOf(targetItemUniqueId);

		if (addAfter || targetIndex===-1) {
			dataItemRefs.splice((targetIndex + 1), 0, sourceItemUniqueId);
			sourceIndex = sourceIndex > targetIndex ? sourceIndex + 1 : sourceIndex;
			dataItemRefs.splice(sourceIndex, 1);
		} else {
			dataItemRefs[sourceIndex] = targetItemUniqueId;
			dataItemRefs[targetIndex] = sourceItemUniqueId;
		}
		this.visualization.getSlots().setDataItems(dataItemRefs, this.slotId, transactionToken);
	}

	//A user moves an item to a new slot to 'add it after' something.
	//(either the another item or the slot heading).
	_moveItemToSlot(sourceSlot, sourceDataItem, addAfter, transactionToken) {
		const sourceDataItemId = sourceDataItem.getId();
		const targetDataItemRefs = this.slot.getDataItemList().map(dataItem => dataItem.getId());
		const targetIndex = this._getTargetIndex(addAfter);
		// Add this data item to the new slot
		targetDataItemRefs.splice(targetIndex, 0, sourceDataItemId);
		this.visualization.getSlots().setDataItems(targetDataItemRefs, this.slotId, transactionToken);
	}

	//This is the case where a user takes a slot and drops it on top of another slot (the two should swap)
	_swapItemsBetweenSlots(sourceSlot, sourceDataItemUniqueId, targetDataItemUniqueId, transactionToken) {
		// Update target slot
		const targetSlotId = this.slotId;
		let targetDataItemRefs = this.slot.getDataItemList().map(dataItem => dataItem.getId());
		const targetIndex = targetDataItemRefs.indexOf(targetDataItemUniqueId);
		targetDataItemRefs[targetIndex] = sourceDataItemUniqueId;

		// Update source slot
		const sourceSlotId = sourceSlot.getDefinition().getId();
		let sourceDataItemRefs = sourceSlot.getDataItemList().map(dataItem => dataItem.getId());
		let sourceIndex = sourceDataItemRefs.indexOf(sourceDataItemUniqueId);
		sourceDataItemRefs[sourceIndex] = targetDataItemUniqueId;

		// swap
		this.visualization.getSlots().setDataItems(targetDataItemRefs, targetSlotId, transactionToken);
		this.visualization.getSlots().setDataItems(sourceDataItemRefs, sourceSlotId, transactionToken);
	}

	/**
	 * @returns the actions associated with this dataItem and slotType
	 */
	getActions() {
		const slots = this.slot.getDataItemList();
		const singleSlot = this._getSingleSlot(slots, this.indexInSlot);
		const slotActions = this.visualization.getSlots().getSlot(this.slotId).getDefinition().getProperty('actions');
		const actionExt = singleSlot ? slotActions : null;
		const options = {};
		if (actionExt && actionExt.length > 0) {
			options.actionsToKeep = actionExt;
		}
		return Promise.resolve(this._getActions(this.slotId, this.indexInSlot, options)
			.map( action => {
				return {
					text: action.label,
					...action
				};
			}));
	}

	/**
	 * Brings up the UI to execute the slot action menu.
	 * @param parentNode - the node to set the menu under.
	 * @param editorOptions - an object to control things like placement of the menu.
	 */
	openSlotActionMenu(parentNode, editorOptions) {
		return this.getActions().then( actions => {
			const actionList = actions.map((action) => {
				return {
					order: action.order || ActionTypes[action.name],
					...action
				};
			}).sort((a, b) => {
				return a.order - b.order;
			});
			this._toolbarHelper.buildActionToolbar(actionList, parentNode, /*label*/null, editorOptions);
			return actionList;
		});
	}

	/* check if action menu is open */
	isSlotActionMenuOpened() {
		return this._toolbarHelper.getToolbar() ? true : false;
	}

	_getSingleSlot(slots, mapIndex) {
		//if slots length is not 1, return null
		//if mapIndex exists and its an array of length > 1, return null
		const isArrayOfMapIndex = mapIndex && mapIndex instanceof Array && mapIndex.length > 1;
		if ( (slots.length !== 1) || isArrayOfMapIndex ) {
			return null;
		}
		return slots[0];
	}
	/**
	 * Set the index in the slot for a data item
	 * @param {int} indexInSlot
	 */
	setIndexInSlot(indexInSlot) {
		this.indexInSlot = indexInSlot;
	}

	getPickTreeContext(includeMetadata) {
		const context = {
			type: this.slot.getDefinition().getType()
		};
		if (includeMetadata && this.indexInSlot > -1 ) {
			context.metadataColumn = this.slot.getDataItemList()[this.indexInSlot].getMetadataColumn();
		}
		return context;
	}
}



// WEBPACK FOOTER //
// ./src/js/VisSlotDataItemHandler.js